/**
 * Role 13 — Analysis of Profit or Loss — calculated totals.
 *
 * Extracted verbatim from the former single-class `CalculationService`, which held
 * all 13 sheets' total calculations in one 5,557-line file. Each function is pure and
 * self-contained (no shared state, no `this`), so the split is a move, not a rewrite.
 * `CalculationService` remains the injectable entry point and delegates here.
 */

/**
 * Calculate all Role13 (310100 - Analysis of Profit or Loss) totals based on form data
 *
 * @param formData - The form data object with control values (format: qname[member][period])
 * @param xbrlPrefix - The XBRL prefix ('ifrs-full' for MFRS, 'ifrs-smes' for MPERS)
 * @returns Object containing only the calculated total fields
 */
export function calculateTotals_310100(
  formData: Record<string, any>,
  xbrlPrefix: 'ifrs-full' | 'ifrs-smes',
): Record<string, any> {
  const calculatedTotals: Record<string, any> = {};

  // Helper function to get field value from formData
  const getFieldValue = (qname: string, suffix: string): number => {
    const value = formData[qname + suffix];

    // Handle null, undefined, or empty string
    if (value === null || value === undefined || value === '') {
      return 0;
    }

    // Convert to number if it's a string
    const numValue = typeof value === 'number' ? value : parseFloat(value);

    // Return 0 if conversion resulted in NaN
    return isNaN(numValue) ? 0 : numValue;
  };

  // Helper function to set calculated value
  const setValue = (qname: string, suffix: string, value: number): void => {
    const controlName = qname + suffix;
    calculatedTotals[controlName] = value;
  };

  // Determine if we're using MFRS or MPERS
  const isMFRS = xbrlPrefix === 'ifrs-full';

  // Calculate for all 4 columns
  const suffixes = [
    '[Consolidated][CurrentYear]',
    '[Consolidated][PreviousYear]',
    '[Separate][CurrentYear]',
    '[Separate][PreviousYear]',
  ];

  suffixes.forEach((suffix) => {
    if (isMFRS) {
      // #region "MFRS"
      // 1. Total revenue from sale of goods
      const totalRevenueFromSaleOfGoods =
        getFieldValue(
          'ifrs-full:RevenueFromSaleOfTelecommunicationEquipment',
          suffix,
        ) +
        getFieldValue('ssmt-mfrs:RevenueFromSaleOfProperty', suffix) +
        getFieldValue(
          'ssmt-mfrs:RevenueFromSaleOfConstructionContracts',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:RevenueFromSaleOfCleanWaterTreatmentAndDisposalOfWasteWater',
          suffix,
        ) +
        getFieldValue('ifrs-full:RevenueFromSaleOfFoodAndBeverage', suffix) +
        getFieldValue(
          'ifrs-full:RevenueFromSaleOfAgriculturalProduce',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:RevenueFromSaleOfOilAndGasProducts',
          suffix,
        ) +
        getFieldValue('ssmt-mfrs:RevenueFromSaleOfOtherGoods', suffix);
      setValue(
        'ifrs-full:RevenueFromSaleOfGoods',
        suffix,
        totalRevenueFromSaleOfGoods,
      );

      // 2. Total revenue from rendering of services
      const totalRevenueFromServices =
        getFieldValue(
          'ssmt-mfrs:RevenueFromEntertainmentOperations',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:RevenueFromRenderingOfTelecommunicationServices',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:RevenueFromRenderingOfTransportServices',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:RevenueFromRenderingOfInformationTechnologyServices',
          suffix,
        ) +
        getFieldValue('ssmt-mfrs:RevenueFromEducationalServices', suffix) +
        getFieldValue('ssmt-mfrs:RevenueFromHealthcareServices', suffix) +
        getFieldValue(
          'ifrs-full:RevenueFromRenderingOfCargoAndMailTransportServices',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:RevenueFromRenderingOfOtherServices',
          suffix,
        );
      setValue(
        'ifrs-full:RevenueFromRenderingOfServices',
        suffix,
        totalRevenueFromServices,
      );

      // 3. Total interest income
      const totalInterestIncome =
        getFieldValue(
          'ifrs-full:InterestIncomeOnLoansAndReceivables',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:InterestIncomeOnOtherFinancialAssets',
          suffix,
        );
      setValue('ifrs-full:RevenueFromInterest', suffix, totalInterestIncome);

      // 4. Total other fee and commission income
      const totalFeeAndCommission =
        getFieldValue('ifrs-full:BrokerageFeeIncome', suffix) +
        getFieldValue(
          'ifrs-full:PortfolioAndOtherManagementFeeIncome',
          suffix,
        ) +
        getFieldValue('ifrs-full:OtherFeeAndCommissionIncome', suffix);
      setValue(
        'ifrs-full:FeeAndCommissionIncome',
        suffix,
        totalFeeAndCommission,
      );

      // 5. Total revenue
      const totalRevenue =
        getFieldValue('ifrs-full:RevenueFromSaleOfGoods', suffix) +
        getFieldValue('ifrs-full:RevenueFromRenderingOfServices', suffix) +
        getFieldValue('ifrs-full:RevenueFromInterest', suffix) +
        getFieldValue('ifrs-full:FeeAndCommissionIncome', suffix) +
        getFieldValue('ifrs-full:RevenueFromDividends', suffix) +
        getFieldValue('ifrs-full:RentalIncome', suffix) +
        getFieldValue('ifrs-full:RevenueFromRoyalties', suffix) +
        getFieldValue('ifrs-full:OtherRevenue', suffix);
      setValue('ifrs-full:Revenue', suffix, totalRevenue);

      // ======================== COST OF SALES ========================

      // 6. Total cost of sales
      const totalCostOfSales =
        getFieldValue('ssmt-mfrs:CostOfInventories', suffix) +
        getFieldValue('ssmt-mfrs:ConstructionContractCost', suffix) +
        getFieldValue('ifrs-full:EnergyExpense', suffix) +
        getFieldValue(
          'ifrs-full:PropertyDevelopmentAndProjectManagementExpense',
          suffix,
        ) +
        getFieldValue('ssmt-mfrs:OtherCostOfSales', suffix);
      setValue('ifrs-full:CostOfSales', suffix, totalCostOfSales);

      // ======================== OTHER INCOME ========================

      // 7. Total foreign exchange gain
      const totalForeignExchangeGain =
        getFieldValue('ssmt-mfrs:RealisedGainOnForeignExchange', suffix) +
        getFieldValue('ssmt-mfrs:UnrealisedGainOnForeignExchange', suffix);
      setValue(
        'ssmt-mfrs:GainsOnForeignExchange',
        suffix,
        totalForeignExchangeGain,
      );

      // 8. Total gain on disposal
      const totalGainOnDisposal =
        getFieldValue('ssmt-mfrs:GainsFromDisposalOfSubsidiaries', suffix) +
        getFieldValue('ssmt-mfrs:GainsFromDisposalOfAssociates', suffix) +
        getFieldValue('ssmt-mfrs:GainsFromDisposalOfJointVentures', suffix);
      setValue(
        'ssmt-mfrs:GainsOnDisposalOfSubsidiaryJointVenturesAndAssociates',
        suffix,
        totalGainOnDisposal,
      );

      // 9. Total reversal of impairment loss
      const totalReversalOfImpairment =
        getFieldValue(
          'ifrs-full:ReversalOfImpairmentLossRecognisedInProfitOrLossLoansAndAdvances',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:ReversalOfImpairmentLossRecognisedInProfitOrLossInvestmentProperty',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:ReversalOfImpairmentLossInLandHeldForDevelopment',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:ReversalOfImpairmentLossRecognisedInProfitOrLossPropertyPlantAndEquipment',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:ReversalOfImpairmentLossForOtherAssets',
          suffix,
        );
      setValue(
        'ifrs-full:ReversalOfImpairmentLossRecognisedInProfitOrLoss',
        suffix,
        totalReversalOfImpairment,
      );

      // 10. Total other income (38 fields total)
      const totalOtherIncome =
        // First 11 fields (before any subsections)
        getFieldValue('ssmt-mfrs:AmortisationOfDeferredIncome', suffix) +
        getFieldValue('ssmt-mfrs:BadDebtsRecovered', suffix) +
        getFieldValue('ssmt-mfrs:OtherIncomeDividend', suffix) +
        getFieldValue('ssmt-mfrs:OtherIncomeRoyaltyAndFranchise', suffix) +
        getFieldValue('ssmt-mfrs:WriteBackOfFuelCost', suffix) +
        getFieldValue(
          'ifrs-full:IncomeFromReimbursementsUnderInsurancePolicies',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:GrantsOrIncentivesReceivedByMalaysianGovernmentOrAgencies',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:GrantsOrIncentivesReceivedByForeignGovernmentOrAgencies',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:ContributionsReceivedByLocalContributor',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:ContributionsReceivedByForeignContributor',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:ContributionsReceivedByUnknownContributor',
          suffix,
        ) +
        // Foreign exchange gain section (calculated subtotal)
        getFieldValue('ssmt-mfrs:GainsOnForeignExchange', suffix) +
        // Gain on disposal section (calculated subtotal + 4 more fields)
        getFieldValue(
          'ssmt-mfrs:GainsOnDisposalOfSubsidiaryJointVenturesAndAssociates',
          suffix,
        ) +
        getFieldValue('ssmt-mfrs:GainsOnDisposalOfOtherInvestments', suffix) +
        getFieldValue(
          'ifrs-full:GainsOnDisposalsOfPropertyPlantAndEquipment',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:GainsOnDisposalsOfNoncurrentAssets',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:OtherGainsRecognisedInProfitOrLossFairValueMeasurementAssets',
          suffix,
        ) +
        // Reversal of impairment section (calculated subtotal)
        getFieldValue(
          'ifrs-full:ReversalOfImpairmentLossRecognisedInProfitOrLoss',
          suffix,
        ) +
        // Remaining 11 fields after reversal section
        getFieldValue('ifrs-full:ReversalOfInventoryWritedown', suffix) +
        getFieldValue('ssmt-mfrs:OtherInterestIncome', suffix) +
        getFieldValue('ssmt-mfrs:IncomeFromManagementFees', suffix) +
        getFieldValue('ssmt-mfrs:NetDepositsRecognised', suffix) +
        getFieldValue(
          'ifrs-full:GainsOnChangeInFairValueOfDerivatives',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:FairValueGainOnRecycleOfForexReserveUponDisposalOfSubsdiaries',
          suffix,
        ) +
        getFieldValue('ssmt-mfrs:OtherFeesAndCommissionIncome', suffix) +
        getFieldValue('ssmt-mfrs:OtherRentalIncome', suffix) +
        getFieldValue(
          'ssmt-mfrs:OtherRentalIncomeOnLandAndBuildings',
          suffix,
        ) +
        getFieldValue('ifrs-full:MiscellaneousOtherOperatingIncome', suffix);
      setValue('ifrs-full:OtherIncome', suffix, totalOtherIncome);

      // ======================== EXPENSES ========================

      // 11. Total auditor's remuneration
      const totalAuditorsRemuneration =
        getFieldValue(
          'ifrs-full:AuditorsRemunerationForAuditServices',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:AuditorsRemunerationForOtherServices',
          suffix,
        );
      setValue(
        'ifrs-full:AuditorsRemuneration',
        suffix,
        totalAuditorsRemuneration,
      );

      // 12. Total loss on disposal
      const totalLossOnDisposal =
        getFieldValue('ssmt-mfrs:LossOnDisposalOfSubsidiaries', suffix) +
        getFieldValue('ssmt-mfrs:LossOnDisposalOfAssociates', suffix) +
        getFieldValue('ssmt-mfrs:LossOnDisposalOfJointVentures', suffix);
      setValue(
        'ssmt-mfrs:LossOnDisposalOfSubsidiariesAssociatesAndJointVentures',
        suffix,
        totalLossOnDisposal,
      );

      // 13. Total employee benefits expense
      const totalEmployeeBenefits =
        getFieldValue('ifrs-full:WagesAndSalaries', suffix) +
        getFieldValue('ssmt-mfrs:BonusExpenses', suffix) +
        getFieldValue(
          'ifrs-full:ExpenseFromSharebasedPaymentTransactionsWithEmployees',
          suffix,
        ) +
        getFieldValue('ssmt-mfrs:EmployeeShareBasedPayment', suffix) +
        getFieldValue('ifrs-full:SocialSecurityContributions', suffix) +
        getFieldValue(
          'ifrs-full:PostemploymentBenefitExpenseDefinedBenefitPlans',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:PostemploymentBenefitExpenseDefinedContributionPlans',
          suffix,
        ) +
        getFieldValue('ifrs-full:OtherLongtermBenefits', suffix) +
        getFieldValue('ifrs-full:OtherShorttermEmployeeBenefits', suffix) +
        getFieldValue('ifrs-full:OtherEmployeeExpense', suffix);
      setValue(
        'ifrs-full:EmployeeBenefitsExpense',
        suffix,
        totalEmployeeBenefits,
      );

      // 14. Total director's remuneration
      const totalDirectorsRemuneration =
        getFieldValue('ssmt-mfrs:WagesAndSalariesToDirectors', suffix) +
        getFieldValue('ssmt-mfrs:BonusToDirectors', suffix) +
        getFieldValue('ssmt-mfrs:BenefitsInKindExpenses', suffix) +
        getFieldValue('ssmt-mfrs:DirectorsFees', suffix) +
        getFieldValue('ssmt-mfrs:PerformanceIncentives', suffix) +
        getFieldValue(
          'ssmt-mfrs:RetirementBenefitsDefinedBenefitPlans',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:RetirementBenefitsDefinedContributionPlans',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:KeyManagementPersonnelCompensationSharebasedPayment',
          suffix,
        ) +
        getFieldValue('ssmt-mfrs:OtherDirectorsRemuneration', suffix);
      setValue(
        'ssmt-mfrs:DirectorsRemuneration',
        suffix,
        totalDirectorsRemuneration,
      );

      // 15. Total other expenses (38 fields total)
      const totalOtherExpenses =
        // Auditor's remuneration section (calculated subtotal)
        getFieldValue('ifrs-full:AuditorsRemuneration', suffix) +
        // 3 fields after auditor's remuneration
        getFieldValue('ifrs-full:AmortisationExpense', suffix) +
        getFieldValue(
          'ifrs-full:DepreciationPropertyPlantAndEquipment',
          suffix,
        ) +
        getFieldValue('ssmt-mfrs:NaturalDisasterRelatedExpenses', suffix) +
        // Loss on disposal section (calculated subtotal + 4 additional fields)
        getFieldValue(
          'ssmt-mfrs:LossOnDisposalOfSubsidiariesAssociatesAndJointVentures',
          suffix,
        ) +
        getFieldValue('ifrs-full:LossesOnDisposalsOfInvestments', suffix) +
        getFieldValue(
          'ifrs-full:LossesOnDisposalsOfPropertyPlantAndEquipment',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:LossesOnDisposalsOfNoncurrentAssets',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:OtherLossesRecognisedInProfitOrLossFairValueMeasurementAssets',
          suffix,
        ) +
        // 2 rental/royalty fields
        getFieldValue('ifrs-full:RentalExpense', suffix) +
        getFieldValue('ifrs-full:RoyaltyExpense', suffix) +
        // Employee benefits expense section (calculated subtotal)
        getFieldValue('ifrs-full:EmployeeBenefitsExpense', suffix) +
        // Director's remuneration section (calculated subtotal)
        getFieldValue('ssmt-mfrs:DirectorsRemuneration', suffix) +
        // Final field
        getFieldValue('ssmt-mfrs:OtherMiscellaneousExpenses', suffix);
      setValue(
        'ifrs-full:OtherExpenseByFunction',
        suffix,
        totalOtherExpenses,
      );

      // 16. Total finance income
      const totalFinanceIncome =
        getFieldValue(
          'ssmt-mfrs:InterestIncomeDueFromRelatedParties',
          suffix,
        ) + getFieldValue('ifrs-full:OtherFinanceIncome', suffix);
      setValue('ifrs-full:FinanceIncome', suffix, totalFinanceIncome);

      // #endregion "MFRS"
    } else {
      // #region "MPERS"

      // 1. Total revenue from sale of goods
      const totalRevenueFromSaleOfGoods =
        getFieldValue(
          'ssmt-mpers:RevenueFromSaleOfTelecommunicationEquipment',
          suffix,
        ) +
        getFieldValue('ssmt-mpers:RevenueFromSaleOfProperty', suffix) +
        getFieldValue(
          'ssmt-mpers:RevenueFromSaleOfConstructionContracts',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:RevenueFromSaleOfCleanWaterTreatmentAndDisposalOfWasteWater',
          suffix,
        ) +
        getFieldValue('ssmt-mpers:RevenueFromSaleOfFoodAndBeverage', suffix) +
        getFieldValue(
          'ssmt-mpers:RevenueFromSaleOfAgriculturalProduce',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:RevenueFromSaleOfOilAndGasProducts',
          suffix,
        ) +
        getFieldValue('ssmt-mpers:RevenueFromSaleOfOtherGoods', suffix);
      setValue(
        'ifrs-smes:RevenueFromSaleOfGoods',
        suffix,
        totalRevenueFromSaleOfGoods,
      );

      // 2. Total revenue from rendering of services
      const totalRevenueFromServices =
        getFieldValue(
          'ssmt-mpers:RevenueFromEntertainmentOperations',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:RevenueFromRenderingOfTelecommunicationServices',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:RevenueFromRenderingOfTransportServices',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:RevenueFromRenderingOfInformationTechnologyServices',
          suffix,
        ) +
        getFieldValue('ssmt-mpers:RevenueFromEducationalServices', suffix) +
        getFieldValue('ssmt-mpers:RevenueFromHealthcareServices', suffix) +
        getFieldValue(
          'ssmt-mpers:RevenueFromRenderingOfCargoAndMailTransportServices',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:RevenueFromRenderingOfOtherServices',
          suffix,
        );
      setValue(
        'ifrs-smes:RevenueFromRenderingOfServices',
        suffix,
        totalRevenueFromServices,
      );

      // 3. Total interest income
      const totalInterestIncome =
        getFieldValue(
          'ssmt-mpers:InterestIncomeOnLoansAndReceivables',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:InterestIncomeOnOtherFinancialAssets',
          suffix,
        );
      setValue('ifrs-smes:RevenueFromInterest', suffix, totalInterestIncome);

      // 4. Total other fee and commission income
      const totalFeeAndCommission =
        getFieldValue('ssmt-mpers:BrokerageFeeIncome', suffix) +
        getFieldValue('ssmt-mpers:OtherFeesAndCommissionIncome', suffix) +
        getFieldValue(
          'ssmt-mpers:PortfolioAndOtherManagementFeeIncome',
          suffix,
        );
      setValue(
        'ssmt-mpers:FeeAndCommissionIncome',
        suffix,
        totalFeeAndCommission,
      );

      // 5. Total revenue
      const totalRevenue =
        getFieldValue('ifrs-smes:RevenueFromSaleOfGoods', suffix) +
        getFieldValue('ifrs-smes:RevenueFromRenderingOfServices', suffix) +
        getFieldValue('ifrs-smes:RevenueFromInterest', suffix) +
        getFieldValue('ssmt-mpers:FeeAndCommissionIncome', suffix) +
        getFieldValue('ifrs-smes:RevenueFromDividends', suffix) +
        getFieldValue('ssmt-mpers:RentalIncome', suffix) +
        getFieldValue('ifrs-smes:RevenueFromRoyalties', suffix) +
        getFieldValue('ifrs-smes:OtherRevenue', suffix);
      setValue('ifrs-smes:Revenue', suffix, totalRevenue);

      // ======================== COST OF SALES ========================

      // 6. Total cost of sales
      const totalCostOfSales =
        getFieldValue('ssmt-mpers:CostOfInventories', suffix) +
        getFieldValue('ssmt-mpers:ConstructionContractCost', suffix) +
        getFieldValue('ssmt-mpers:EnergyExpense', suffix) +
        getFieldValue(
          'ssmt-mpers:PropertyDevelopmentAndProjectManagementExpense',
          suffix,
        ) +
        getFieldValue('ssmt-mpers:OtherCostOfSales', suffix);
      setValue('ifrs-smes:CostOfSales', suffix, totalCostOfSales);

      // ======================== OTHER INCOME ========================

      // 7. Total foreign exchange gain
      const totalForeignExchangeGain =
        getFieldValue('ssmt-mpers:RealisedGainOnForeignExchange', suffix) +
        getFieldValue('ssmt-mpers:UnrealisedGainOnForeignExchange', suffix);
      setValue(
        'ssmt-mpers:GainsOnForeignExchange',
        suffix,
        totalForeignExchangeGain,
      );

      // 8. Total gain on disposal
      const totalGainOnDisposal =
        getFieldValue('ssmt-mpers:GainsFromDisposalOfSubsidiaries', suffix) +
        getFieldValue('ssmt-mpers:GainsFromDisposalOfAssociates', suffix) +
        getFieldValue('ssmt-mpers:GainsFromDisposalOfJointVentures', suffix);
      setValue(
        'ssmt-mpers:GainsOnDisposalOfSubsidiaryJointVenturesAndAssociates',
        suffix,
        totalGainOnDisposal,
      );

      // 9. Total reversal of impairment loss
      const totalReversalOfImpairment =
        getFieldValue(
          'ssmt-mpers:ReversalOfImpairmentLossRecognisedInProfitOrLossLoansAndAdvances',
          suffix,
        ) +
        getFieldValue('ifrs-smes:ReversalOfInventoryWritedown', suffix) +
        getFieldValue(
          'ifrs-smes:ReversalOfImpairmentLossRecognisedInProfitOrLossIntangibleAssetsOtherThanGoodwill',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:ReversalOfImpairmentLossRecognisedInProfitOrLossGoodwill',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:ReversalOfImpairmentLossRecognisedInProfitOrLossPropertyPlantAndEquipment',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:ReversalOfImpairmentLossRecognisedInProfitOrLossAssociates',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:ReversalOfImpairmentLossRecognisedInProfitOrLossJointVentures',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:ReversalOfImpairmentLossForOtherAssets',
          suffix,
        );
      setValue(
        'ifrs-smes:ReversalOfImpairmentLossRecognisedInProfitOrLoss',
        suffix,
        totalReversalOfImpairment,
      );

      // 10. Total other income
      const totalOtherIncome =
        // First 3 fields
        getFieldValue('ssmt-mpers:AmortisationOfDeferredIncome', suffix) +
        getFieldValue('ssmt-mpers:BadDebtsRecovered', suffix) +
        getFieldValue('ssmt-mpers:OtherIncomeDividend', suffix) +
        // Foreign exchange gain section (calculated subtotal)
        getFieldValue('ssmt-mpers:GainsOnForeignExchange', suffix) +
        // Gain on disposal section (calculated subtotal + 4 more fields)
        getFieldValue(
          'ssmt-mpers:GainsOnDisposalOfSubsidiaryJointVenturesAndAssociates',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:GainsOnDisposalOfOtherInvestments',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:GainsOnDisposalsOfPropertyPlantAndEquipment',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:GainsOnDisposalsOfNoncurrentAssets',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:OtherGainsRecognisedInProfitOrLossFairValueMeasurementAssets',
          suffix,
        ) +
        getFieldValue('ssmt-mpers:OtherInterestIncome', suffix) +
        getFieldValue('ssmt-mpers:IncomeFromManagementFees', suffix) +
        getFieldValue(
          'ifrs-smes:GainsLossesOnFinancialAssetsAtFairValueThroughProfitOrLoss',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:FairValueGainOnRecycleOfForexReserveUponDisposalOfSubsdiaries',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:MiscellaneousOtherFeesAndCommissionIncome',
          suffix,
        ) +
        getFieldValue('ssmt-mpers:OtherRentalIncome', suffix) +
        getFieldValue(
          'ssmt-mpers:OtherRentalIncomeOnLandAndBuildings',
          suffix,
        ) +
        // Reversal of impairment section (calculated subtotal - already includes ReversalOfInventoryWritedown)
        getFieldValue(
          'ifrs-smes:ReversalOfImpairmentLossRecognisedInProfitOrLoss',
          suffix,
        ) +
        // Fields after reversal section
        getFieldValue('ssmt-mpers:OtherRoyaltyIncome', suffix) +
        getFieldValue(
          'ssmt-mpers:GrantsOrIncentivesReceivedByMalaysianGovernmentOrAgencies',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:GrantsOrIncentivesReceivedByForeignGovernmentOrAgencies',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:ContributionsReceivedByLocalContributor',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:ContributionsReceivedByForeignContributor',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:ContributionsReceivedByUnknownContributor',
          suffix,
        ) +
        getFieldValue('ssmt-mpers:OtherMiscellaneousIncome', suffix);
      setValue('ifrs-smes:OtherIncome', suffix, totalOtherIncome);

      // ======================== EXPENSES ========================

      // 11. Total auditor's remuneration
      const totalAuditorsRemuneration =
        getFieldValue(
          'ssmt-mpers:AuditorsRemunerationForAuditServices',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:AuditorsRemunerationForOtherServices',
          suffix,
        );
      setValue(
        'ssmt-mpers:AuditorsRemuneration',
        suffix,
        totalAuditorsRemuneration,
      );

      // 12. Total loss on disposal
      const totalLossOnDisposal =
        getFieldValue('ssmt-mpers:LossOnDisposalOfSubsidiaries', suffix) +
        getFieldValue('ssmt-mpers:LossOnDisposalOfAssociates', suffix) +
        getFieldValue('ssmt-mpers:LossOnDisposalOfJointVentures', suffix);
      setValue(
        'ssmt-mpers:LossOnDisposalOfSubsidiariesAssociatesAndJointVentures',
        suffix,
        totalLossOnDisposal,
      );

      // 13. Total employee benefits expense
      const totalEmployeeBenefits =
        getFieldValue('ifrs-smes:WagesAndSalaries', suffix) +
        getFieldValue('ssmt-mpers:BonusExpenses', suffix) +
        getFieldValue(
          'ssmt-mpers:ExpenseFromSharebasedPaymentTransactionsWithEmployees',
          suffix,
        ) +
        getFieldValue('ssmt-mpers:EmployeeShareBasedPayment', suffix) +
        getFieldValue('ifrs-smes:SocialSecurityContributions', suffix) +
        getFieldValue('ifrs-smes:TerminationBenefitsExpense', suffix) +
        getFieldValue(
          'ifrs-smes:PostemploymentBenefitExpenseDefinedContributionPlans',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:PostemploymentBenefitExpenseDefinedBenefitPlans',
          suffix,
        ) +
        getFieldValue('ifrs-smes:OtherLongtermBenefits', suffix) +
        getFieldValue('ifrs-smes:OtherShorttermEmployeeBenefits', suffix) +
        getFieldValue('ifrs-smes:OtherEmployeeExpense', suffix);
      setValue(
        'ifrs-smes:EmployeeBenefitsExpense',
        suffix,
        totalEmployeeBenefits,
      );

      // 14. Total director's remuneration
      const totalDirectorsRemuneration =
        getFieldValue('ssmt-mpers:WagesAndSalariesToDirectors', suffix) +
        getFieldValue('ssmt-mpers:BonusToDirectors', suffix) +
        getFieldValue(
          'ssmt-mpers:KeyManagementPersonnelCompensationSharebasedPayment',
          suffix,
        ) +
        getFieldValue('ssmt-mpers:BenefitsInKindExpenses', suffix) +
        getFieldValue('ssmt-mpers:DirectorsFees', suffix) +
        getFieldValue('ssmt-mpers:PerformanceIncentives', suffix) +
        getFieldValue(
          'ssmt-mpers:RetirementBenefitsDefinedContributionPlans',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:RetirementBenefitsDefinedBenefitPlans',
          suffix,
        ) +
        getFieldValue('ssmt-mpers:OtherDirectorsRemuneration', suffix);
      setValue(
        'ssmt-mpers:DirectorsRemuneration',
        suffix,
        totalDirectorsRemuneration,
      );

      // 15. Total other expenses
      const totalOtherExpenses =
        // Auditor's remuneration section (calculated subtotal)
        getFieldValue('ssmt-mpers:AuditorsRemuneration', suffix) +
        // 3 fields after auditor's remuneration
        getFieldValue('ssmt-mpers:AmortisationExpense', suffix) +
        getFieldValue(
          'ifrs-smes:DepreciationPropertyPlantAndEquipment',
          suffix,
        ) +
        getFieldValue('ssmt-mpers:NaturalDisasterRelatedExpenses', suffix) +
        // Loss on disposal section (calculated subtotal)
        getFieldValue(
          'ssmt-mpers:LossOnDisposalOfSubsidiariesAssociatesAndJointVentures',
          suffix,
        ) +
        getFieldValue('ssmt-mpers:LossesOnDisposalsOfInvestments', suffix) +
        getFieldValue(
          'ssmt-mpers:LossesOnDisposalsOfPropertyPlantAndEquipment',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:LossesOnDisposalsOfNoncurrentAssets',
          suffix,
        ) +
        // 3 additional expense fields
        getFieldValue(
          'ssmt-mpers:LossOnRevaluationOfFinancialInstruments',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:ProvisionForLiabilitiesAndCharges',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:ProvisionForGuaranteeAndWarranties',
          suffix,
        ) +
        // Rental and royalty fields
        getFieldValue('ssmt-mpers:RentalExpense', suffix) + // 130
        getFieldValue('ssmt-mpers:RoyaltyExpense', suffix) + // 131
        // Employee benefits expense section (calculated subtotal)
        getFieldValue('ifrs-smes:EmployeeBenefitsExpense', suffix) +
        // Director's remuneration section (calculated subtotal)
        getFieldValue('ssmt-mpers:DirectorsRemuneration', suffix) +
        // Final field
        getFieldValue('ssmt-mpers:OtherMiscellaneousExpenses', suffix);
      setValue(
        'ifrs-smes:OtherExpenseByFunction',
        suffix,
        totalOtherExpenses,
      );

      // 16. Total finance income
      const totalFinanceIncome =
        getFieldValue(
          'ssmt-mpers:InterestIncomeDueFromRelatedParties',
          suffix,
        ) + getFieldValue('ssmt-mpers:OtherFinanceIncome', suffix);
      setValue('ifrs-smes:FinanceIncome', suffix, totalFinanceIncome);

      // #endregion "MPERS"
    }
  });

  return calculatedTotals;
}
